import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { wishlistService, WishlistItem } from '../services/wishlistService';
import { cartService } from '../services/cartService';

const WishlistPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<WishlistItem[]>([]);

  useEffect(() => {
    if (user) setItems(wishlistService.getWishlist(user.id));
  }, [user]);

  const handleRemove = (productId: string) => {
    if (!user) return;
    setItems(wishlistService.removeFromWishlist(user.id, productId));
  };

  const handleClear = () => {
    if (!user || !window.confirm('Clear your entire wishlist?')) return;
    wishlistService.clearWishlist(user.id);
    setItems([]);
  };

  const handleAddToCart = (item: WishlistItem) => {
    if (!user) { navigate('/login'); return; }
    cartService.addToCart(user.id, {
      productId: item.productId,
      name: item.name,
      price: item.price,
      imageUrl: item.imageUrl,
      category: item.category,
      quantity: 1,
    });
    alert(`${item.name} added to cart!`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-2">Please login to view your wishlist</h2>
          <button onClick={() => navigate('/login')} className="bg-[#DAA520] text-white px-6 py-2 rounded hover:bg-[#c5941c] mt-4">
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <div className="py-8 px-4 md:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">
              Your Wishlist {items.length > 0 && <span className="text-lg font-normal text-gray-500">({items.length} items)</span>}
            </h1>
            {items.length > 0 && (
              <button onClick={handleClear} className="text-red-600 hover:text-red-800 font-medium text-sm">Clear All</button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <div className="text-6xl mb-4">🤍</div>
              <h3 className="text-xl font-semibold mb-2">Your wishlist is empty</h3>
              <p className="text-gray-600 mb-6">Tap the heart on any product to save it here.</p>
              <button onClick={() => navigate('/')} className="bg-[#DAA520] text-white px-6 py-2 rounded hover:bg-[#c5941c]">
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {items.map(item => (
                <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative">
                    <img src={encodeURI(item.imageUrl)} alt={item.name} className="w-full h-52 object-cover cursor-pointer" onClick={() => navigate(item.detailUrl)} />
                    <button onClick={() => handleRemove(item.productId)} className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform" title="Remove from wishlist">
                      <svg className="w-4 h-4 text-red-500 fill-red-500" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 mb-1 truncate cursor-pointer hover:text-[#DAA520]" onClick={() => navigate(item.detailUrl)}>{item.name}</h3>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-bold text-[#DAA520]">₹{item.price.toFixed(0)}</span>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded capitalize">{item.category}</span>
                    </div>
                    <p className="text-gray-500 text-xs mb-4 line-clamp-2">{item.description}</p>
                    <button onClick={() => handleAddToCart(item)} className="w-full bg-[#DAA520] text-white py-2 rounded font-medium hover:bg-[#c5941c] text-sm">
                      Add to Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WishlistPage;
